import { useMemo, useState } from "react";
import {
  CalendarDays,
  CheckSquare,
  FileText,
  Flag,
  Layers,
  Lightbulb,
  Receipt,
  Search,
  Zap,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";

const CARD_BORDER = "hsl(210,30%,85%)";
const PAGE_SIZE = 50;

type TimelineItem = {
  id: string;
  type: string;
  title: string;
  date: string;
  status: string | null;
  owner: string | null;
  description: string | null;
  entityContext: {
    productName?: string;
    projectName?: string;
    meetingTitle?: string;
  };
  isKeyEvent: boolean;
};

type TimelineTypeConfig = {
  key: string;
  label: string;
  icon: typeof Flag;
  color: string;
  bg: string;
  defaultOn: boolean;
};

const TIMELINE_TYPES: TimelineTypeConfig[] = [
  {
    key: "milestone",
    label: "Milestone",
    icon: Flag,
    color: "hsl(142,72%,28%)",
    bg: "hsl(142,55%,92%)",
    defaultOn: true,
  },
  {
    key: "meeting",
    label: "Meeting",
    icon: CalendarDays,
    color: "hsl(210,100%,28%)",
    bg: "hsl(210,55%,92%)",
    defaultOn: true,
  },
  {
    key: "task",
    label: "Task",
    icon: CheckSquare,
    color: "hsl(215,16%,40%)",
    bg: "hsl(210,16%,93%)",
    defaultOn: false,
  },
  {
    key: "action",
    label: "Action",
    icon: Zap,
    color: "hsl(38,92%,30%)",
    bg: "hsl(38,92%,92%)",
    defaultOn: true,
  },
  {
    key: "idea",
    label: "Idea",
    icon: Lightbulb,
    color: "hsl(262,83%,38%)",
    bg: "hsl(262,55%,92%)",
    defaultOn: true,
  },
  {
    key: "expense",
    label: "Expense",
    icon: Receipt,
    color: "hsl(25,95%,40%)",
    bg: "hsl(25,95%,92%)",
    defaultOn: false,
  },
  {
    key: "note",
    label: "Note",
    icon: FileText,
    color: "hsl(175,60%,30%)",
    bg: "hsl(175,55%,92%)",
    defaultOn: true,
  },
];

const TYPE_MAP = new Map(TIMELINE_TYPES.map((t) => [t.key, t]));

function getTypeConfig(type: string): TimelineTypeConfig {
  return (
    TYPE_MAP.get(type) ?? {
      key: type,
      label: type,
      icon: Layers,
      color: "hsl(215,16%,40%)",
      bg: "hsl(210,16%,93%)",
      defaultOn: true,
    }
  );
}

function formatTimelineDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

type Mode = "all" | "key";
type TimeRange = "7" | "30" | "quarter" | "all";

function getDateFrom(range: TimeRange): string | undefined {
  if (range === "all") return undefined;
  const now = new Date();
  if (range === "7") {
    now.setDate(now.getDate() - 7);
  } else if (range === "30") {
    now.setDate(now.getDate() - 30);
  } else {
    const q = Math.floor(now.getMonth() / 3) * 3;
    now.setMonth(q, 1);
    now.setHours(0, 0, 0, 0);
  }
  return now.toISOString();
}

function getEndpointPath(
  entityType: "company" | "product" | "project",
  entityId: string,
): string {
  switch (entityType) {
    case "company":
      return `/companies/${entityId}/timeline`;
    case "product":
      return `/products/${entityId}/timeline`;
    case "project":
      return `/projects/${entityId}/timeline`;
  }
}

type Props = {
  entityType: "company" | "product" | "project";
  entityId: string;
};

export function TimelineTab({ entityType, entityId }: Props) {
  const { accessToken } = useAuth();
  const [mode, setMode] = useState<Mode>("all");
  const [activeTypes, setActiveTypes] = useState<Set<string>>(
    () => new Set(TIMELINE_TYPES.filter((t) => t.defaultOn).map((t) => t.key)),
  );
  const [timeRange, setTimeRange] = useState<TimeRange>("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const typesParam = useMemo(() => {
    if (mode === "key") return "milestone,meeting";
    return Array.from(activeTypes).join(",");
  }, [mode, activeTypes]);

  const queryParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      types: typesParam || undefined,
      dateFrom: getDateFrom(timeRange),
      search: search.trim() || undefined,
    }),
    [page, typesParam, timeRange, search],
  );

  const { data, isLoading } = useQuery({
    queryKey: [entityType, entityId, "timeline", queryParams, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!entityId,
    queryFn: () =>
      apiFetch<{ data: TimelineItem[]; total: number }>(
        getEndpointPath(entityType, entityId),
        {
          token: accessToken,
          query: {
            page: queryParams.page,
            limit: queryParams.limit,
            types: queryParams.types,
            dateFrom: queryParams.dateFrom,
            search: queryParams.search,
          },
        },
      ),
  });

  const items = data?.data ?? [];
  const total = data?.total ?? 0;
  const hasMore = page * PAGE_SIZE < total;

  function toggleType(key: string) {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setPage(1);
  }

  return (
    <div className="space-y-4">
      {/* Header toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Mode toggle */}
        <div className="flex rounded-lg border" style={{ borderColor: CARD_BORDER }}>
          <button
            type="button"
            onClick={() => setMode("all")}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${
              mode === "all"
                ? "bg-primary text-primary-foreground"
                : "bg-white text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
            } rounded-l-lg`}
          >
            All Activity
          </button>
          <button
            type="button"
            onClick={() => setMode("key")}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${
              mode === "key"
                ? "bg-primary text-primary-foreground"
                : "bg-white text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
            } rounded-r-lg`}
          >
            Key Events
          </button>
        </div>

        {/* Type filter chips (only in "all" mode) */}
        {mode === "all" && (
          <div className="flex flex-wrap items-center gap-1.5">
            {TIMELINE_TYPES.map((t) => {
              const active = activeTypes.has(t.key);
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => toggleType(t.key)}
                  className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors"
                  style={{
                    borderColor: active ? t.color : CARD_BORDER,
                    background: active ? t.bg : "white",
                    color: active ? t.color : "hsl(215,16%,50%)",
                  }}
                >
                  <t.icon className="h-3 w-3" strokeWidth={1.5} />
                  {t.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Time range */}
        <Select
          className="w-36"
          value={timeRange}
          onChange={(e) => {
            setTimeRange(e.target.value as TimeRange);
            setPage(1);
          }}
        >
          <option value="all">All time</option>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="quarter">This quarter</option>
        </Select>

        {/* Search */}
        <div className="relative w-52">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            strokeWidth={1.5}
          />
          <Input
            placeholder="Search timeline..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-9 text-xs"
          />
        </div>

        {/* Event count */}
        <span className="ml-auto text-xs text-muted-foreground">
          {total} event{total !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Timeline feed */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="h-20 animate-pulse rounded-xl border bg-[hsl(210,30%,97%)]"
              style={{ borderColor: CARD_BORDER }}
            />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-sm text-muted-foreground">
          No activity found for the selected filters.
        </div>
      ) : (
        <div className="relative ml-4 border-l-2" style={{ borderColor: CARD_BORDER }}>
          {items.map((item) => (
            <TimelineEvent key={`${item.type}-${item.id}`} item={item} />
          ))}
        </div>
      )}

      {/* Show more */}
      {hasMore && (
        <div className="flex justify-center pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
          >
            Show more
          </Button>
        </div>
      )}
      {!isLoading && items.length > 0 && !hasMore && (
        <div className="py-4 text-center text-xs text-muted-foreground">
          No more events
        </div>
      )}
    </div>
  );
}

function TimelineEvent({ item }: { item: TimelineItem }) {
  const config = getTypeConfig(item.type);
  const Icon = config.icon;

  return (
    <div className="relative mb-4 ml-6">
      {/* Icon circle on the timeline line */}
      <div
        className="absolute -left-[37px] flex h-6 w-6 items-center justify-center rounded-full"
        style={{ background: config.bg, color: config.color }}
      >
        <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
      </div>

      {/* Content card */}
      <div
        className="rounded-xl border bg-white p-4"
        style={{ borderColor: CARD_BORDER }}
      >
        {/* Top row */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <span
              className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
              style={{ background: config.bg, color: config.color }}
            >
              {config.label}
            </span>
            <span className="text-sm font-medium text-foreground">
              {item.title}
            </span>
          </div>
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {formatTimelineDate(item.date)}
          </span>
        </div>

        {/* Second row */}
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          {item.status && (
            <span
              className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
              style={{
                background: "hsl(210,16%,93%)",
                color: "hsl(215,16%,40%)",
              }}
            >
              {item.status}
            </span>
          )}
          {item.owner && (
            <span className="text-xs text-muted-foreground">{item.owner}</span>
          )}
          {item.entityContext.productName && (
            <span className="text-[10px] text-muted-foreground">
              · {item.entityContext.productName}
            </span>
          )}
          {item.entityContext.projectName && (
            <span className="text-[10px] text-muted-foreground">
              · {item.entityContext.projectName}
            </span>
          )}
        </div>

        {/* Description preview */}
        {item.description && (
          <p className="mt-1.5 text-xs text-muted-foreground line-clamp-2">
            {item.description}
          </p>
        )}
      </div>
    </div>
  );
}
